import { useCallback } from 'react';

import { gql, useApolloClient, useMutation } from '@apollo/client';

import { useTeam } from '../../context/TeamProvider';
import {
  toAssignableContentIds,
  unassignableContentIdsMessage,
} from '../../routes/settings/labels/data/labelCategoryMapper';
import type {
  NexoyaMutationManuallyAssignContentLabelArgs,
  NexoyaMutationRemoveContentLabelManualAssignmentsArgs,
} from '../../types';

import { CONTENT_LABEL_MUTATION_CONTEXT, activeLabelAssignmentReads, evictCategoryStages } from './mutationEffects';

/**
 * Manual assignment — the human half of the labelling loop: settling a
 * "needs decision" clash, bulk-assigning from the contents table, or taking a
 * label back off.
 *
 * Both inputs take `contentIds: [Float!]!` while every content id in the UI
 * domain is a string, so these hooks accept strings and convert at the boundary
 * with `toAssignableContentIds`. That keeps the one conversion in one tested
 * place instead of a `Number(...)` at each call site, where a non-numeric id
 * would serialise as `null` and fail the non-null list.
 *
 * A conversion that cannot account for every selected id is fatal here: sending
 * the shortened list instead would be a mutation that returns 200 having
 * labelled fewer contents than the user selected — or none of them — with
 * nothing to tell them apart from success. Both callbacks are `async`, so the
 * failure arrives as a rejected promise on the same path a network or GraphQL
 * error would, and `mutate` is never called.
 *
 * Either mutation moves contents between the category's stages in a way only the
 * server can compute (a manual pick overrides the rules; removing one hands the
 * content back to them), so a `true` answer evicts that category's stage reads
 * and asks every label-assignment read on screen again — Settings' counts and
 * open stage pages, AND the portfolio Content tab's per-page cells, which no
 * eviction can reach (see `mutationEffects.ts` for both halves of why).
 *
 * KNOWN SCOPE LINE: the category and value mutations still refetch the stage
 * reads alone, so deleting a label value while the Content tab is open leaves a
 * stale chip there until the page is revisited. That widening belongs with the
 * Settings surface.
 */

export const MANUALLY_ASSIGN_CONTENT_LABEL_MUTATION = gql`
  mutation ManuallyAssignContentLabel($teamId: Int!, $assign: ManuallyAssignContentLabelInput!) {
    manuallyAssignContentLabel(teamId: $teamId, assign: $assign)
  }
`;

export const REMOVE_CONTENT_LABEL_MANUAL_ASSIGNMENTS_MUTATION = gql`
  mutation RemoveContentLabelManualAssignments($teamId: Int!, $remove: RemoveContentLabelManualAssignmentsInput!) {
    removeContentLabelManualAssignments(teamId: $teamId, remove: $remove)
  }
`;

export type ManuallyAssignContentLabelResponse = { manuallyAssignContentLabel: boolean };
export type RemoveContentLabelManualAssignmentsResponse = { removeContentLabelManualAssignments: boolean };

export function useManuallyAssignContentLabel() {
  const { teamId } = useTeam();
  const client = useApolloClient();

  const [mutate, result] = useMutation<
    ManuallyAssignContentLabelResponse,
    NexoyaMutationManuallyAssignContentLabelArgs
  >(MANUALLY_ASSIGN_CONTENT_LABEL_MUTATION, { context: CONTENT_LABEL_MUTATION_CONTEXT });

  const manuallyAssignContentLabel = useCallback(
    async ({
      contentLabelCategoryId,
      contentLabelCategoryValueId,
      contentIds,
    }: {
      contentLabelCategoryId: string;
      contentLabelCategoryValueId: string;
      contentIds: string[];
    }) => {
      const assignable = toAssignableContentIds(contentIds);
      if (assignable.status === 'unassignable') {
        throw new Error(unassignableContentIdsMessage(assignable.unconvertible));
      }

      return mutate({
        variables: {
          teamId,
          assign: {
            contentLabelCategoryId,
            contentLabelCategoryValueId,
            contentIds: assignable.ids,
          },
        },
        update(cache, { data }) {
          if (data?.manuallyAssignContentLabel) evictCategoryStages(cache, contentLabelCategoryId);
        },
        refetchQueries: ({ data }) => (data?.manuallyAssignContentLabel ? activeLabelAssignmentReads(client) : []),
      });
    },
    [client, mutate, teamId],
  );

  return [manuallyAssignContentLabel, result] as const;
}

export function useRemoveContentLabelManualAssignments() {
  const { teamId } = useTeam();
  const client = useApolloClient();

  const [mutate, result] = useMutation<
    RemoveContentLabelManualAssignmentsResponse,
    NexoyaMutationRemoveContentLabelManualAssignmentsArgs
  >(REMOVE_CONTENT_LABEL_MANUAL_ASSIGNMENTS_MUTATION, { context: CONTENT_LABEL_MUTATION_CONTEXT });

  /**
   * Removal is category-wide, not per label: the input carries no value id, so
   * this clears whatever the human had assigned in that category and hands the
   * contents back to the rules.
   */
  const removeContentLabelManualAssignments = useCallback(
    async ({ contentLabelCategoryId, contentIds }: { contentLabelCategoryId: string; contentIds: string[] }) => {
      const assignable = toAssignableContentIds(contentIds);
      if (assignable.status === 'unassignable') {
        throw new Error(unassignableContentIdsMessage(assignable.unconvertible, 'remove content labels'));
      }

      return mutate({
        variables: {
          teamId,
          remove: { contentLabelCategoryId, contentIds: assignable.ids },
        },
        update(cache, { data }) {
          if (data?.removeContentLabelManualAssignments) evictCategoryStages(cache, contentLabelCategoryId);
        },
        refetchQueries: ({ data }) =>
          data?.removeContentLabelManualAssignments ? activeLabelAssignmentReads(client) : [],
      });
    },
    [client, mutate, teamId],
  );

  return [removeContentLabelManualAssignments, result] as const;
}
